import { createContext, useCallback, useContext, useState } from 'react'

export interface ViewedGym {
  id: string
  slug: string
}

interface ViewedGymValue {
  /** The gym the member is working with: the one in the URL, or the last one they opened. */
  gym: ViewedGym | null
  setGym: (gym: ViewedGym) => void
}

const STORAGE_KEY = 'sc:viewed-gym'

// Survives a reload so a member who opens /profile directly still gets their gym
// menu. Storage can throw in private modes, so every access is guarded.
function readStored(): ViewedGym | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ViewedGym>
    return parsed.id && parsed.slug ? { id: parsed.id, slug: parsed.slug } : null
  } catch {
    return null
  }
}

const ViewedGymContext = createContext<ViewedGymValue>({
  gym: null,
  setGym: () => {},
})

/**
 * Bridges the viewed gym from GymLayout (which resolves it from the slug) up to
 * RootLayout's sidebar, so the News badge counts the viewed gym's news rather
 * than the JWT's primary gym — important for multi-gym members.
 *
 * It is deliberately NOT cleared when leaving a gym route. The global pages
 * (/profile, /our-gyms) carry no slug, and forgetting the gym there would strip
 * a member's whole menu the moment they opened their profile.
 */
export function ViewedGymProvider({ children }: { children: React.ReactNode }) {
  const [gym, setGymState] = useState<ViewedGym | null>(readStored)

  const setGym = useCallback((next: ViewedGym) => {
    setGymState((current) =>
      current?.id === next.id && current.slug === next.slug ? current : next,
    )
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
    } catch {
      // A remembered gym is a convenience; losing it is not an error.
    }
  }, [])

  return <ViewedGymContext.Provider value={{ gym, setGym }}>{children}</ViewedGymContext.Provider>
}

export function useViewedGym(): ViewedGymValue {
  return useContext(ViewedGymContext)
}
